import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FundWiseLogo from './FundWiseLogo';
import { CheckCircle, AlertCircle, Mail, RefreshCw } from 'lucide-react';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const verificationAttempted = useRef(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmail(token);
    } else if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    // Prevent multiple calls if already processing
    if (status !== 'verifying') {
      return;
    }
    
    try {
      console.log('🔄 Starting email verification with token:', verificationToken);
      const response = await axios.post('/auth/verify-email', {
        token: verificationToken
      });
      
      console.log('✅ Email verification successful:', response.data);
      setStatus('success');
      setMessage(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.log('❌ Email verification failed:', error.response?.data || error.message);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Email verification failed. The link may be expired or invalid.');
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      alert('Please enter your email address');
      return;
    }

    setResendLoading(true);
    try {
      const response = await axios.post('/auth/resend-verification', {
        email: email
      });
      
      alert(response.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <FundWiseLogo size="lg" variant="default" />
          </Link>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            {status === 'verifying' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Verifying Your Email</h2>
                <p className="text-slate-600">Please wait while we verify your email address...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Email Verified!</h2>
                <p className="text-slate-600 mb-4">{message}</p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 text-sm">
                    🎉 Redirecting to login page in 3 seconds...
                  </p>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h2>
                <p className="text-slate-600 mb-6">{message}</p>

                {/* Resend Verification */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Need a new verification link?</h3>
                  <div className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your email address"
                        data-testid="resend-email-input"
                      />
                    </div>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="resend-verification-btn"
                    >
                      {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Navigation Links */}
          <div className="text-center space-y-2">
            <Link 
              to="/login" 
              className="block text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              data-testid="go-to-login"
            >
              Go to Login
            </Link>
            <Link 
              to="/" 
              className="block text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="back-to-home"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;